var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { openDB, } from 'idb';
const IDB_ERROR_OPTIONS = {
    tags: {
        vkdoc_error_type: 'idb',
    },
};
const TIMESTAMP_INDEX = 'timestamp';
// eslint-disable-next-line no-magic-numbers
const DEFAULT_CACHE_EXPIRATION_DURATION = 1000 * 60 * 60 * 24 * 14;
/**
 * Базовый абстрактный сервис для кэш-сервисов на IndexedDB.
 *
 * Открытие БД с переподключением при обрыве соединения.
 * Очистка протухших записей и обёртку с логированием ошибок.
 * Наследники задают `database`/`tableName` и реализуют чтение/запись под свою
 * доменную логику, переиспользуя унаследованный `init`.
 */
export class IdbCacheService {
    constructor(logger) {
        this.logger = logger;
        this.db = null;
        this.initPromise = null;
        this.version = 1;
        this.expirationDuration = DEFAULT_CACHE_EXPIRATION_DURATION;
    }
    init() {
        return __awaiter(this, arguments, void 0, function* (tableName = this.tableName) {
            if (this.db) {
                return this.db;
            }
            if (!this.initPromise) {
                this.initPromise = this.openDatabase(tableName).finally(() => {
                    this.initPromise = null;
                });
            }
            return this.initPromise;
        });
    }
    openDatabase(tableName) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                this.db = yield openDB(this.database, this.version, {
                    upgrade: (db) => {
                        if (!db.objectStoreNames.contains(tableName)) {
                            const store = db.createObjectStore(tableName, {
                                keyPath: 'id',
                            });
                            store.createIndex(TIMESTAMP_INDEX, TIMESTAMP_INDEX);
                        }
                    },
                });
                this.db.onclose = () => {
                    this.db = null;
                };
                this.db.onversionchange = () => {
                    var _a;
                    (_a = this.db) === null || _a === void 0 ? void 0 : _a.close();
                    this.db = null;
                };
                yield this.clearTableIfExpired(tableName);
                return this.db;
            }
            catch (error) {
                this.logger.error(error, IDB_ERROR_OPTIONS);
                this.db = null;
                return null;
            }
        });
    }
    clearTableIfExpired() {
        return __awaiter(this, arguments, void 0, function* (tableName = this.tableName) {
            try {
                if (!this.db) {
                    return null;
                }
                const tx = this.db.transaction(tableName, 'readwrite');
                const range = IDBKeyRange.upperBound(Date.now() - this.expirationDuration);
                const cursor = yield tx
                    .objectStore(tableName)
                    .index(TIMESTAMP_INDEX)
                    .openCursor(range);
                if (cursor) {
                    yield this.db.clear(tableName);
                }
                return null;
            }
            catch (error) {
                this.logger.error(error, IDB_ERROR_OPTIONS);
                return null;
            }
        });
    }
    clearTable() {
        return __awaiter(this, arguments, void 0, function* (tableName = this.tableName) {
            var _a;
            try {
                const db = yield this.init(tableName);
                return (_a = (yield (db === null || db === void 0 ? void 0 : db.clear(tableName)))) !== null && _a !== void 0 ? _a : null;
            }
            catch (error) {
                this.logger.error(error, IDB_ERROR_OPTIONS);
                return null;
            }
        });
    }
    deleteValue(id_1) {
        return __awaiter(this, arguments, void 0, function* (id, tableName = this.tableName) {
            try {
                const db = yield this.init(tableName);
                const tx = db === null || db === void 0 ? void 0 : db.transaction(tableName, 'readwrite');
                const store = tx === null || tx === void 0 ? void 0 : tx.objectStore(tableName);
                return yield (store === null || store === void 0 ? void 0 : store.delete(id));
            }
            catch (error) {
                this.logger.error(error, IDB_ERROR_OPTIONS);
                return null;
            }
        });
    }
}
